import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import { JsonViewer } from "@/components/common/JsonViewer";
import { Eye, Loader2, RefreshCw, ScrollText, Search } from "lucide-react";
import { usePaymentGatewayEvents } from "@/hooks/usePaymentGatewayEventQueries";
import { formatDate } from "@/lib/utils";
import { getPaginationItems } from "@/lib/pagination";
import type {
  PaymentGatewayEvent,
  PaymentGatewayEventListFilters,
  PaymentGatewayEventType,
} from "@/types/payment-gateway-event";
import { usePaymentGatewayEventFiltersStore } from "@/stores/paymentGatewayEventFiltersStore";
import {
  PAYMENT_GATEWAY_EVENT_COLUMNS,
  usePaymentGatewayEventColumnsStore,
  type PaymentGatewayEventColumnId,
} from "@/stores/paymentGatewayEventColumnsStore";

/** Shown wherever a row has no value for a field. */
const EMPTY = "—";

// Declared as a Record so adding a column without a width is a compile error. Sized to what each
// column carries: a WIC-PAY code is 27 characters, a refusal reason up to a short sentence.
const COLUMN_WIDTHS: Record<PaymentGatewayEventColumnId, string> = {
  occurredAt: "w-[170px]",
  eventType: "w-[150px]",
  outcome: "w-[190px]",
  paymentCode: "w-[270px]",
  user: "w-[240px]",
  gatewayCode: "w-[180px]",
  amount: "w-[130px]",
  detail: "w-[300px]",
  payload: "w-[90px]",
};

/** What an operator calls each direction. */
const EVENT_LABELS: Record<PaymentGatewayEventType, string> = {
  PaymentRequest: "Bill request",
  Callback: "Webhook",
};

/** Outcomes that mean the message did what it was for. */
const SUCCESS_OUTCOMES = new Set(["Issued", "Settled"]);

/** A redelivery of a bill settled earlier: nothing happened, and nothing is wrong. */
const NEUTRAL_OUTCOMES = new Set(["AlreadySettled"]);

/** A log is read to the second: a bill request and its webhook can share a minute. */
function formatLogTime(value: string): string {
  const date = new Date(value);
  const two = (part: number) => String(part).padStart(2, "0");
  return `${formatDate(date)} ${two(date.getHours())}:${two(date.getMinutes())}:${two(date.getSeconds())}`;
}

/** The gateway's amounts are whole đồng in smallest unit; grouped for reading, no symbol. */
function formatAmount(amount: number | null): string {
  if (amount === null) return EMPTY;
  return new Intl.NumberFormat("en-US").format(amount);
}

/** The stored message as an object, or the raw text when it is not valid JSON. */
function parsePayload(payload: string): unknown {
  try {
    return JSON.parse(payload);
  } catch {
    return payload;
  }
}

/**
 * T-PayGate → Webhook Log.
 *
 * The messages exchanged with the payment gateway, newest first: every bill WiCloud asked it to
 * issue and every webhook it sent back, each with how it ended. One search box, matched exactly
 * against the payment code (WIC-PAY-…) or the gateway's own code.
 */
export function TPaygateWebhookLogPage() {
  // Filters live in the shared table store, like every other admin list.
  const { search } = usePaymentGatewayEventFiltersStore(
    (state) => state.filters,
  );
  const page = usePaymentGatewayEventFiltersStore((state) => state.page);
  const pageSize = usePaymentGatewayEventFiltersStore(
    (state) => state.pageSize,
  );
  const setFilter = usePaymentGatewayEventFiltersStore(
    (state) => state.setFilter,
  );
  const setPage = usePaymentGatewayEventFiltersStore((state) => state.setPage);
  const setPageSize = usePaymentGatewayEventFiltersStore(
    (state) => state.setPageSize,
  );
  const resetStoreFilters = usePaymentGatewayEventFiltersStore(
    (state) => state.resetFilters,
  );
  const hasActiveFilters = usePaymentGatewayEventFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  // `hidden` rather than the store's `isVisible`: it is the value that changes on a toggle.
  const hiddenColumns = usePaymentGatewayEventColumnsStore(
    (state) => state.hidden,
  );
  const toggleColumn = usePaymentGatewayEventColumnsStore(
    (state) => state.toggle,
  );
  const showAllColumns = usePaymentGatewayEventColumnsStore(
    (state) => state.showAll,
  );
  const isVisible = (id: PaymentGatewayEventColumnId) =>
    !hiddenColumns.includes(id);

  // The input holds what is being typed; the store holds what the query runs with.
  const [searchInput, setSearchInput] = useState(search);
  const [selectedEvent, setSelectedEvent] =
    useState<PaymentGatewayEvent | null>(null);

  // Debounced so a pasted code does not fire a request per keystroke. 750ms matches every other
  // admin list screen.
  useEffect(() => {
    // Compared trimmed: a stray space is not a new search, and committing the same term again
    // would still send the list back to page 1.
    const term = searchInput.trim();
    if (term === search) return;

    const timer = setTimeout(() => {
      setFilter("search", term);
    }, 750);

    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  const filters: PaymentGatewayEventListFilters = useMemo(
    () => ({ search: search || undefined, page, pageSize }),
    [search, page, pageSize],
  );

  const {
    data: response,
    isFetching,
    error,
    refetch,
  } = usePaymentGatewayEvents(filters);

  // A term typed but not yet committed: nothing is fetching and the table still shows the previous
  // result, so the box itself says the search is on its way.
  const isSearchPending = searchInput.trim() !== search;

  const events = response?.data ?? [];

  // Fetching with nothing on screen yet. That is the case skeleton rows are for; with rows in hand
  // the pill takes over.
  const isReloading = isFetching && events.length === 0;
  const totalPages = response?.pagination?.totalPages ?? 1;
  const totalCount = response?.pagination?.totalItems ?? 0;

  // The store's page, not the response's: the query keeps placeholder data, so while a page change
  // is in flight `response` still describes the page being replaced.
  const currentPage = page;

  // The search box holds its own copy of what is being typed, so clearing the store alone would
  // leave the old term sitting in the input.
  const resetFilters = () => {
    setSearchInput("");
    resetStoreFilters();
  };

  // One button for both jobs, as on the other admin lists: clear the search if there is one,
  // otherwise re-read the current view.
  const handleRefresh = () => {
    if (hasActiveFilters) {
      resetFilters();
    } else {
      void refetch();
    }
  };

  // Only take the screen over when there is nothing to show. With a response in hand the failure
  // is a banner above the table.
  if (error && !response) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-destructive-ink">
                Failed to load the webhook log
              </p>
              <Button onClick={() => refetch()} className="mt-4">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the Card, not the viewport. `!isReloading` keeps the two loading signals
            mutually exclusive. */}
        {isFetching && !isReloading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="w-full sm:w-[360px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                {isSearchPending && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
                <Input
                  id="search-webhook-log"
                  type="text"
                  placeholder="Payment code (WIC-PAY-…) or bill code"
                  aria-label="Search by payment code or bill code"
                  value={searchInput}
                  // The backend refuses a longer term with a 400.
                  maxLength={100}
                  onChange={(event) => setSearchInput(event.target.value)}
                  className="h-9 pl-10 pr-9"
                />
              </div>
            </div>

            <div className="ml-auto">
              <ColumnSettingsMenu
                columns={PAYMENT_GATEWAY_EVENT_COLUMNS}
                isVisible={isVisible}
                toggle={toggleColumn}
                showAll={showAllColumns}
                hiddenCount={hiddenColumns.length}
                className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {/* Reached only with a previous result in hand — the no-data case returned above. */}
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}
          {isReloading ? (
            <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              <ScrollText className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? "No results found" : "Nothing logged yet"}
              </h3>
              <p className="mb-6 text-muted-foreground">
                {hasActiveFilters
                  ? "Codes are matched exactly. Paste the whole payment code or bill code."
                  : "Bill requests sent to the gateway and the webhooks it sends back will appear here."}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={resetFilters}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <WebhookLogTable
              events={events}
              isVisible={isVisible}
              onViewPayload={setSelectedEvent}
            />
          )}

          {/* Outside the three branches above, so a search that matched nothing still shows its
              pager, its page-size picker and the reload button. */}
          {!isReloading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value, 10))}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage >= totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Icon only; the tooltip says what the click does: it clears the search when
                    there is one and only reloads when there is none. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedEvent && (
        <WebhookLogPayloadDialog
          event={selectedEvent}
          open={selectedEvent !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Green for a message that did what it was for, grey for a redelivery that changed nothing, red
 * for everything else: a refusal or a failure means money or a bill that somebody has to look at.
 */
function OutcomeBadge({ outcome }: { outcome: string }) {
  let color = "bg-destructive-soft text-destructive-ink";
  if (SUCCESS_OUTCOMES.has(outcome)) {
    color = "bg-ok-soft text-ok-ink";
  } else if (NEUTRAL_OUTCOMES.has(outcome)) {
    color = "bg-secondary text-muted-foreground";
  }

  return (
    <Badge variant="outline" className={`px-1.5 py-0 text-[11px] ${color}`}>
      {outcome}
    </Badge>
  );
}

/** The direction of the message, with a mark on a webhook an operator sent from the simulator. */
function EventLabel({ event }: { event: PaymentGatewayEvent }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-sm">
      {/* A webhook is the gateway reporting money in, so it takes the payment code's colour and
          stands out among the bill requests. */}
      <span
        className={event.eventType === "Callback" ? "text-primary" : undefined}
      >
        {EVENT_LABELS[event.eventType] ?? event.eventType}
      </span>
      {event.simulatedBy && (
        <Badge
          variant="outline"
          className="bg-warn-soft px-1.5 py-0 text-[11px] text-foreground"
          title={`Sent from the simulator by ${event.simulatedBy}`}
        >
          Simulated
        </Badge>
      )}
    </span>
  );
}

/** The WIC-PAY code, linked to its payment when the log knows which one it is. */
function PaymentCode({ event }: { event: PaymentGatewayEvent }) {
  if (!event.paymentId) {
    return (
      <span className="font-mono text-xs select-all">
        {event.merchantReference}
      </span>
    );
  }

  return (
    <Link
      to={`/admin/payments/${event.paymentId}`}
      className="font-mono text-xs text-primary hover:underline"
    >
      {event.merchantReference}
    </Link>
  );
}

/** Who created the row's payment: the e-mail, with the phone number under it. */
function PaymentCreator({ event }: { event: PaymentGatewayEvent }) {
  if (!event.createdByEmail && !event.createdByPhone) {
    return <span className="text-xs text-muted-foreground">{EMPTY}</span>;
  }

  return (
    <div className="min-w-0 text-xs">
      <p className="truncate" title={event.createdByEmail ?? ""}>
        {event.createdByEmail || EMPTY}
      </p>
      {event.createdByPhone && (
        <p className="truncate text-muted-foreground tabular-nums">
          {event.createdByPhone}
        </p>
      )}
    </div>
  );
}

interface WebhookLogTableProps {
  events: PaymentGatewayEvent[];
  isVisible: (id: PaymentGatewayEventColumnId) => boolean;
  onViewPayload: (event: PaymentGatewayEvent) => void;
}

function WebhookLogTable({
  events,
  isVisible,
  onViewPayload,
}: WebhookLogTableProps) {
  return (
    <>
      {/* Mobile: one card per row — the desktop column set does not survive a phone width. */}
      <div className="min-h-0 flex-1 overflow-auto rounded-md border md:hidden">
        <Table>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id}>
                <TableCell>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <OutcomeBadge outcome={event.outcome} />
                      <EventLabel event={event} />
                    </div>
                    <PaymentCode event={event} />
                    {(event.createdByEmail || event.createdByPhone) && (
                      <PaymentCreator event={event} />
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {event.gatewayReference && (
                        <span className="font-mono">
                          {event.gatewayReference}
                        </span>
                      )}
                      <span>{formatAmount(event.amount)}</span>
                      <span>{formatLogTime(event.occurredAt)}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 gap-1 px-2 text-xs"
                        onClick={() => onViewPayload(event)}
                      >
                        <Eye className="h-3 w-3" />
                        Payload
                      </Button>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* `[&>div]:overflow-visible` switches off the scroll `components/ui/table.tsx` puts on its
          own wrapper, so this height-bounded div owns both axes and the header can stick. */}
      <div className="scrollbar-thin hidden min-h-0 flex-1 overflow-auto rounded-md border md:block [&>div]:overflow-visible">
        <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
          <colgroup>
            {PAYMENT_GATEWAY_EVENT_COLUMNS.filter((column) =>
              isVisible(column.id),
            ).map((column) => (
              <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
            ))}
          </colgroup>
          <TableHeader className="sticky top-0 z-30 bg-card">
            <TableRow>
              {isVisible("occurredAt") && <TableHead>Time</TableHead>}
              {isVisible("eventType") && <TableHead>Event</TableHead>}
              {isVisible("outcome") && <TableHead>Outcome</TableHead>}
              {isVisible("paymentCode") && <TableHead>Payment code</TableHead>}
              {isVisible("user") && <TableHead>User</TableHead>}
              {isVisible("gatewayCode") && <TableHead>Gateway code</TableHead>}
              {isVisible("amount") && (
                <TableHead className="text-right">Amount</TableHead>
              )}
              {isVisible("detail") && <TableHead>Detail</TableHead>}
              {isVisible("payload") && (
                <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id}>
                {isVisible("occurredAt") && (
                  <TableCell>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatLogTime(event.occurredAt)}
                    </span>
                  </TableCell>
                )}
                {isVisible("eventType") && (
                  <TableCell>
                    <EventLabel event={event} />
                  </TableCell>
                )}
                {isVisible("outcome") && (
                  <TableCell>
                    <OutcomeBadge outcome={event.outcome} />
                  </TableCell>
                )}
                {isVisible("paymentCode") && (
                  <TableCell>
                    <PaymentCode event={event} />
                  </TableCell>
                )}
                {isVisible("user") && (
                  <TableCell>
                    <PaymentCreator event={event} />
                  </TableCell>
                )}
                {isVisible("gatewayCode") && (
                  <TableCell>
                    <span className="font-mono text-xs select-all">
                      {event.gatewayReference || EMPTY}
                    </span>
                  </TableCell>
                )}
                {isVisible("amount") && (
                  <TableCell className="text-right text-sm tabular-nums">
                    {formatAmount(event.amount)}
                  </TableCell>
                )}
                {isVisible("detail") && (
                  <TableCell>
                    <p
                      className="truncate text-xs text-muted-foreground"
                      title={event.outcomeDetail ?? ""}
                    >
                      {event.outcomeDetail || EMPTY}
                    </p>
                  </TableCell>
                )}
                {isVisible("payload") && (
                  <TableCell className="sticky right-0 z-10 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="View payload"
                      onClick={() => onViewPayload(event)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className="col-span-2 min-w-0 break-words">{value}</div>
    </div>
  );
}

interface WebhookLogPayloadDialogProps {
  event: PaymentGatewayEvent;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** One row in full: its columns, and the message exactly as it was exchanged. */
function WebhookLogPayloadDialog({
  event,
  open,
  onOpenChange,
}: WebhookLogPayloadDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {EVENT_LABELS[event.eventType] ?? event.eventType} details
          </DialogTitle>
        </DialogHeader>

        <DialogBody withoutFooter className="space-y-4">
          <DialogDescription>
            What was exchanged with the gateway, and how it ended
          </DialogDescription>
          <div className="flex flex-wrap items-center gap-2">
            <OutcomeBadge outcome={event.outcome} />
            <EventLabel event={event} />
          </div>

          {event.outcomeDetail && (
            <p className="rounded-md border px-3 py-2 text-sm break-words">
              {event.outcomeDetail}
            </p>
          )}

          <Separator />

          <div className="space-y-2">
            <Field label="Time" value={formatLogTime(event.occurredAt)} />
            <Field label="Gateway" value={event.gateway} />
            <Field label="Payment code" value={<PaymentCode event={event} />} />
            <Field
              label="Created by"
              value={<PaymentCreator event={event} />}
            />
            <Field
              label="Gateway code"
              value={
                <span className="font-mono text-xs select-all">
                  {event.gatewayReference || EMPTY}
                </span>
              }
            />
            <Field label="Amount" value={formatAmount(event.amount)} />
            {event.simulatedBy && (
              <Field label="Simulated by" value={event.simulatedBy} />
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Payload</div>
            <JsonViewer value={parsePayload(event.payload)} />
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
